import { useState } from 'react';
import { useFetch, useClasses, useSubmit } from '../hooks';
import { Table, Modal, PageHeader, Select, FormField } from '../components/UI';
import { Plus } from 'lucide-react';
import api from '../utils/api';

const DAYS = ['monday','tuesday','wednesday','thursday','friday','saturday'];

export function Classes() {
  const { classes, loading, refetch } = useClasses();
  const { data: teachers } = useFetch('/teachers');
  const [showAdd, setShowAdd] = useState(false);
  const [showSub, setShowSub] = useState(false);
  const [selClass, setSelClass] = useState(null);
  const [form, setForm] = useState({ name:'', section:'', class_teacher_id:'' });
  const [subForm, setSubForm] = useState({ name:'', code:'', class_id:'', teacher_id:'', max_marks:100 });
  const [subs, setSubs] = useState([]);
  const { loading: saving, submit } = useSubmit();

  const teacherOpts = teachers?.map(t => ({ value: t.teacher_id, label: t.full_name })) || [];
  const classOpts = classes.map(c => ({ value: c.id, label: `${c.name} - ${c.section}` }));

  const loadSubs = async (cls) => {
    setSelClass(cls);
    const { data } = await api.get(`/classes/${cls.id}/subjects`);
    setSubs(data);
  };

  const columns = [
    { label: 'Class', render: r => <span style={{fontWeight:700}}>{r.name} - {r.section}</span> },
    { label: 'Class Teacher', render: r => r.teacher_name || '—' },
    { label: 'Students', render: r => <span style={{fontWeight:700, color:'var(--primary)'}}>{r.strength}</span> },
    { label: 'Year', key: 'academic_year' },
    { label: '', render: r => <button className="btn btn-secondary btn-sm" onClick={() => loadSubs(r)}>Subjects</button> },
  ];

  return (
    <div>
      <PageHeader title="Classes & Subjects" subtitle="Manage class sections and subject assignments"
        actions={<>
          <button className="btn btn-secondary" onClick={() => setShowSub(true)}><Plus size={15}/> Add Subject</button>
          <button className="btn btn-primary" onClick={() => setShowAdd(true)}><Plus size={15}/> Add Class</button>
        </>} />
      <div className="card"><Table columns={columns} data={classes} loading={loading}/></div>

      {/* Subjects panel */}
      {selClass && (
        <div className="card" style={{marginTop:20}}>
          <div className="card-header">
            <span className="card-title">Subjects: {selClass.name} - {selClass.section}</span>
            <button className="btn btn-ghost btn-sm" onClick={() => setSelClass(null)}>✕ Close</button>
          </div>
          <div style={{display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(220px,1fr))', gap:12}}>
            {subs.map(s => (
              <div key={s.id} style={{padding:'12px 16px', background:'var(--surface-alt)', borderRadius:'var(--r-md)', border:'1px solid var(--border)'}}>
                <div style={{fontWeight:700}}>{s.name}</div>
                <div style={{fontSize:'0.75rem',color:'var(--text-muted)'}}>Code: {s.code}</div>
                <div style={{fontSize:'0.78rem',color:'var(--text-secondary)',marginTop:4}}>👨‍🏫 {s.teacher_name || 'Not assigned'}</div>
                <div style={{fontSize:'0.75rem',color:'var(--text-muted)'}}>Max: {s.max_marks} marks</div>
              </div>
            ))}
            {!subs.length && <p style={{color:'var(--text-muted)',fontSize:'0.85rem'}}>No subjects added yet</p>}
          </div>
        </div>
      )}

      <Modal open={showAdd} onClose={() => setShowAdd(false)} title="Add Class" size="sm"
        footer={<><button className="btn btn-secondary" onClick={() => setShowAdd(false)}>Cancel</button>
          <button className="btn btn-primary" disabled={saving} onClick={async () => {
            const r = await submit(() => api.post('/classes', form), 'Class created!');
            if (r.success) { setShowAdd(false); refetch(); }
          }}>{saving ? 'Creating...' : 'Create Class'}</button></>}>
        <div className="form-grid">
          <FormField label="Class Name" required><input className="form-control" value={form.name} onChange={e => setForm(f => ({...f, name:e.target.value}))} placeholder="Class 10"/></FormField>
          <FormField label="Section" required><input className="form-control" value={form.section} onChange={e => setForm(f => ({...f, section:e.target.value}))} placeholder="A"/></FormField>
          <FormField label="Class Teacher"><Select value={form.class_teacher_id} onChange={v => setForm(f => ({...f, class_teacher_id:v}))} options={teacherOpts} placeholder="Assign Teacher"/></FormField>
        </div>
      </Modal>

      <Modal open={showSub} onClose={() => setShowSub(false)} title="Add Subject" size="sm"
        footer={<><button className="btn btn-secondary" onClick={() => setShowSub(false)}>Cancel</button>
          <button className="btn btn-primary" disabled={saving} onClick={async () => {
            const r = await submit(() => api.post('/classes/subjects', subForm), 'Subject added!');
            if (r.success) { setShowSub(false); if (selClass) { const {data} = await api.get(`/classes/${selClass.id}/subjects`); setSubs(data); } }
          }}>{saving ? 'Adding...' : 'Add Subject'}</button></>}>
        <div className="form-grid">
          <FormField label="Subject Name" required><input className="form-control" value={subForm.name} onChange={e => setSubForm(f => ({...f, name:e.target.value}))}/></FormField>
          <FormField label="Subject Code" required><input className="form-control" value={subForm.code} onChange={e => setSubForm(f => ({...f, code:e.target.value}))}/></FormField>
          <FormField label="Class" required><Select value={subForm.class_id} onChange={v => setSubForm(f => ({...f, class_id:v}))} options={classOpts} placeholder="Select Class"/></FormField>
          <FormField label="Teacher"><Select value={subForm.teacher_id} onChange={v => setSubForm(f => ({...f, teacher_id:v}))} options={teacherOpts} placeholder="Assign Teacher"/></FormField>
          <FormField label="Max Marks"><input className="form-control" type="number" value={subForm.max_marks} onChange={e => setSubForm(f => ({...f, max_marks:e.target.value}))}/></FormField>
        </div>
      </Modal>
    </div>
  );
}

export function Timetable() {
  const { classes } = useClasses();
  const [classId, setClassId] = useState('');
  const { data: tt, loading, refetch } = useFetch(classId ? `/timetable?class_id=${classId}` : null, [classId]);
  const [showAdd, setShowAdd] = useState(false);
  const [subjects, setSubjects] = useState([]);
  const { data: teachers } = useFetch('/teachers');
  const [form, setForm] = useState({ class_id:'', subject_id:'', teacher_id:'', day_of_week:'monday', start_time:'', end_time:'', room:'' });
  const { loading: saving, submit } = useSubmit();

  const classOpts = classes.map(c => ({ value: c.id, label: `${c.name} - ${c.section}` }));
  const teacherOpts = teachers?.map(t => ({ value: t.teacher_id, label: t.full_name })) || [];
  const dayOpts = DAYS.map(d => ({ value:d, label: d.charAt(0).toUpperCase()+d.slice(1) }));

  const loadSubjects = async (cid) => {
    setForm(f => ({...f, class_id:cid}));
    if (!cid) return;
    const { data } = await api.get(`/classes/${cid}/subjects`);
    setSubjects(data);
  };

  // Group timetable by day
  const byDay = {};
  DAYS.forEach(d => { byDay[d] = (tt || []).filter(s => s.day_of_week === d).sort((a,b) => a.start_time.localeCompare(b.start_time)); });

  return (
    <div>
      <PageHeader title="Timetable" subtitle="School schedule management"
        actions={<>
          <Select value={classId} onChange={setClassId} options={classOpts} placeholder="Select Class to View"/>
          <button className="btn btn-primary" onClick={() => setShowAdd(true)}><Plus size={15}/> Add Slot</button>
        </>} />

      {classId && (
        <div className="card">
          {loading ? <div className="loading-screen"><div className="spinner spinner-dark"/></div> : (
            <div style={{overflowX:'auto'}}>
              <table style={{width:'100%',borderCollapse:'collapse',minWidth:700}}>
                <thead>
                  <tr>
                    <th style={{background:'var(--sidebar-bg)',color:'#fff',padding:'10px 12px',fontSize:'0.75rem',fontWeight:700}}>Time</th>
                    {DAYS.map(d => <th key={d} style={{background:'var(--sidebar-bg)',color:'#fff',padding:'10px 12px',fontSize:'0.75rem',fontWeight:700,textTransform:'capitalize'}}>{d}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {[...new Set((tt||[]).map(s => s.start_time))].sort().map(time => (
                    <tr key={time}>
                      <td style={{padding:'8px 12px',fontWeight:700,fontSize:'0.78rem',color:'var(--text-secondary)',borderBottom:'1px solid var(--border)',background:'var(--surface-alt)',whiteSpace:'nowrap'}}>{time.slice(0,5)}</td>
                      {DAYS.map(d => {
                        const slot = byDay[d].find(s => s.start_time === time);
                        return <td key={d} style={{padding:'6px',borderBottom:'1px solid var(--border)',borderLeft:'1px solid var(--border)'}}>
                          {slot ? (
                            <div style={{background:'var(--primary-light)',borderRadius:'var(--r-sm)',padding:'6px 8px',borderLeft:'3px solid var(--primary)'}}>
                              <div style={{fontWeight:700,fontSize:'0.78rem',color:'var(--primary)'}}>{slot.subject_name}</div>
                              <div style={{fontSize:'0.7rem',color:'var(--text-secondary)'}}>{slot.teacher_name}</div>
                              {slot.room && <div style={{fontSize:'0.68rem',color:'var(--text-muted)'}}>Room: {slot.room}</div>}
                            </div>
                          ) : null}
                        </td>;
                      })}
                    </tr>
                  ))}
                  {!(tt||[]).length && <tr><td colSpan={7} style={{textAlign:'center',padding:30,color:'var(--text-muted)'}}>No timetable found for this class</td></tr>}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {!classId && <div className="card"><div className="empty-state"><div style={{fontSize:'3rem',marginBottom:12}}>📅</div><p>Select a class to view its timetable</p></div></div>}

      <Modal open={showAdd} onClose={() => setShowAdd(false)} title="Add Timetable Slot" size="sm"
        footer={<><button className="btn btn-secondary" onClick={() => setShowAdd(false)}>Cancel</button>
          <button className="btn btn-primary" disabled={saving} onClick={async () => {
            const r = await submit(() => api.post('/timetable', form), 'Slot added!');
            if (r.success) { setShowAdd(false); refetch(); }
          }}>{saving ? 'Adding...' : 'Add Slot'}</button></>}>
        <div className="form-grid">
          <FormField label="Class" required><Select value={form.class_id} onChange={loadSubjects} options={classOpts} placeholder="Select Class"/></FormField>
          <FormField label="Subject" required><Select value={form.subject_id} onChange={v => setForm(f => ({...f, subject_id:v}))} options={subjects.map(s => ({value:s.id,label:s.name}))} placeholder="Select Subject"/></FormField>
          <FormField label="Teacher" required><Select value={form.teacher_id} onChange={v => setForm(f => ({...f, teacher_id:v}))} options={teacherOpts} placeholder="Select Teacher"/></FormField>
          <FormField label="Day"><Select value={form.day_of_week} onChange={v => setForm(f => ({...f, day_of_week:v}))} options={dayOpts}/></FormField>
          <FormField label="Start Time"><input className="form-control" type="time" value={form.start_time} onChange={e => setForm(f => ({...f, start_time:e.target.value}))}/></FormField>
          <FormField label="End Time"><input className="form-control" type="time" value={form.end_time} onChange={e => setForm(f => ({...f, end_time:e.target.value}))}/></FormField>
          <FormField label="Room"><input className="form-control" value={form.room} onChange={e => setForm(f => ({...f, room:e.target.value}))}/></FormField>
        </div>
      </Modal>
    </div>
  );
}
